import { useMemo, useState } from "react";
import { useLingui } from "@lingui/react";
import { Link, useLocation } from "react-router";
import { ArrowRightIcon } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { DeltaBadge } from "@/components/ui/stat-card";
import { ChartTableToggle } from "@/components/cockpit/ChartTableToggle";
import { EmptyChart } from "@/components/cockpit/EmptyChart";
import { ExportButtons } from "@/components/cockpit/explorer/tables/ExportButtons";
import { exportBaseName } from "@/lib/metrics/csv";
import { formatMetricValue, metricStatDelta, periodTotalCaption } from "@/lib/metrics/metricFormat";
import { getMetric } from "@/lib/metrics/registry";
import type { MetricScopeParams } from "@/lib/metrics/series";
import { useMetricSeries } from "@/lib/metrics/useMetricData";
import { useSlowLoading } from "@/hooks/useSlowLoading";
import type { MetricDefinition } from "@/lib/metrics/types";
import type { ViewMode } from "@/types/cockpit";
import { MetricChart } from "./MetricChart";
import { MetricMonthTable, monthRows, useMonthColumns } from "./MetricMonthTable";
import { isOrgWideUnderScope, metricDetailHref } from "./metricLinks";

export interface MetricPanelCardProps {
  /** A registry metric id (`arrival_pickup`, `workflow_items`, ...). */
  metricId: string;
  from: string;
  to: string;
  scope: MetricScopeParams;
  currency: string;
  /** Chart height; the silo cards sit two side by side above 1440px. */
  height?: number;
  /** Replaces the metric's label as the card title. */
  title?: string;
  /** Where "Open in Metrics" leads; outside Analytics the caller passes it. */
  detailHref?: string;
  /** False skips every request (a user who may not read the metric). */
  enabled?: boolean;
}

/**
 * One registry metric inside a silo (#2175 D3, D4, D5): the period figure with
 * its change, the twelve months as a chart or a table (exportable), and a link
 * into the metric's detail in the Kennzahlen silo. Same requests and figures as
 * the hub card and the detail, so the three agree.
 */
export function MetricPanelCard({
  metricId,
  from,
  to,
  scope,
  currency,
  height = 240,
  title,
  detailHref,
  enabled = true,
}: MetricPanelCardProps) {
  const def = getMetric(metricId) as MetricDefinition;
  const { i18n } = useLingui();
  const location = useLocation();
  const [view, setView] = useState<ViewMode>("chart");
  const series = useMetricSeries(def, from, to, scope, { enabled });
  const months = useMemo(() => monthRows(series.points), [series.points]);
  const columns = useMonthColumns(def, series.points, currency);
  const label = i18n._(def.label);
  const value = formatMetricValue(def.unit, series.total, i18n, currency);
  const orgWide = isOrgWideUnderScope(def.scope, scope);
  const delta = metricStatDelta(def, series.delta, i18n);
  const slow = useSlowLoading(series.query.isLoading);

  return (
    <Card data-testid={`metric-panel-${def.id}`} className="min-w-0">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2 space-y-0 pb-2">
        <div className="min-w-0 space-y-1">
          <CardTitle className="text-base">{title ?? label}</CardTitle>
          <CardDescription>
            {i18n._(def.basis)}
            {orgWide ? ` ${i18n._({ id: "analytics.metrics.scope.allNote", message: "(all locations)" })}` : null}
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {view === "table" ? (
            <ExportButtons
              rows={months}
              columns={columns.exportColumns}
              filename={exportBaseName(label, from, to, "")}
              sheetName={label.slice(0, 31)}
            />
          ) : null}
          <ChartTableToggle mode={view} onModeChange={setView} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {series.query.isError ? (
          <QueryErrorState compact error={series.query.error} onRetry={() => void series.query.refetch()} />
        ) : series.query.isLoading ? (
          <div>
            <div className="h-60 animate-pulse rounded-md bg-muted" aria-hidden="true" />
            {slow ? (
              <p role="status" className="mt-2 text-xs text-muted-foreground">
                {i18n._({ id: "common.stillLoading", message: "Still loading…" })}
              </p>
            ) : null}
          </div>
        ) : series.points.length === 0 ? (
          <EmptyChart
            message={i18n._({ id: "analytics.metrics.noData", message: "No data in this period" })}
            hint={i18n._({
              id: "analytics.metrics.noDataHint",
              message: "No data in this period. Choose a longer period or another scope.",
            })}
          />
        ) : (
          <>
            <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
              <span className="text-2xl font-semibold tabular-nums">{value || "–"}</span>
              {delta ? <DeltaBadge delta={delta} /> : null}
              <span className="text-muted-foreground">
                {periodTotalCaption(def.aggregate, from, to, series.lastPeriod, i18n)}
              </span>
            </p>
            {view === "chart" ? (
              <MetricChart def={def} points={series.points} currency={currency} height={height} />
            ) : (
              <MetricMonthTable def={def} rows={months} columns={columns.columns} isLoading={false} />
            )}
          </>
        )}
        <Link
          to={detailHref ?? metricDetailHref(location.pathname, location.search, def.id)}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          {i18n._({ id: "analytics.metrics.openDetail", message: "Open in Metrics" })}
          <ArrowRightIcon aria-hidden="true" className="size-4" />
        </Link>
      </CardContent>
    </Card>
  );
}
